/**
 * Calendar & sharing utilities for appointments
 * ICS generation, Google Calendar links, WhatsApp & email sharing
 */

/**
 * Pad date/time components to ICS format: YYYYMMDDTHHMMSS
 */
const toICSDateTime = (dateStr, timeStr) => {
  const [year, month, day] = dateStr.split('-');
  const [hours, minutes] = timeStr.split(':');
  return `${year}${month}${day}T${hours}${minutes}00`;
};

/**
 * Add minutes to a date+time and return ICS-formatted string
 */
const addMinutesToICS = (dateStr, timeStr, minutes) => {
  const dt = new Date(`${dateStr}T${timeStr}:00`);
  dt.setMinutes(dt.getMinutes() + minutes);
  const y = dt.getFullYear();
  const mo = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  const h = String(dt.getHours()).padStart(2, '0');
  const mi = String(dt.getMinutes()).padStart(2, '0');
  return `${y}${mo}${d}T${h}${mi}00`;
};

/**
 * Format date for display in messages: "12/01/2025"
 */
const formatDateForMessage = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Generate ICS file content for an appointment
 */
export const generateICS = ({ title, date, time, durationMinutes = 30, location, description }) => {
  const dtStart = toICSDateTime(date, time);
  const dtEnd = addMinutesToICS(date, time, durationMinutes);
  const now = new Date();
  const stamp = toICSDateTime(
    now.toISOString().slice(0, 10),
    now.toISOString().slice(11, 16)
  );

  const escapeICS = (str) => (str || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Carmin//Appointments//FR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `DTSTAMP:${stamp}`,
    `UID:${Date.now()}-${Math.random().toString(36).slice(2)}@carmin.app`,
    `SUMMARY:${escapeICS(title)}`,
  ];

  if (description) lines.push(`DESCRIPTION:${escapeICS(description)}`);
  if (location) lines.push(`LOCATION:${escapeICS(location)}`);

  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n');
};

/**
 * Download an .ics file for an appointment
 */
export const downloadICS = (appointment, vehicle) => {
  const make = vehicle?.make || '';
  const model = vehicle?.model || '';
  const title = `Visite ${make} ${model}`.trim();
  const seller = appointment.is_seller ? appointment.buyer : appointment.seller;
  const description = [
    `Visite du véhicule ${make} ${model}`,
    seller?.display_name ? `Contact : ${seller.display_name}` : '',
    seller?.phone ? `Tél : ${seller.phone}` : '',
  ].filter(Boolean).join('\\n');

  const icsContent = generateICS({
    title,
    date: appointment.date,
    time: appointment.time,
    durationMinutes: 30,
    description,
  });

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `rdv-${make}-${model}-${appointment.date}.ics`.toLowerCase().replace(/\s+/g, '-');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate a Google Calendar URL for an appointment
 */
export const getGoogleCalendarUrl = (appointment, vehicle) => {
  const make = vehicle?.make || '';
  const model = vehicle?.model || '';
  const title = `Visite ${make} ${model}`.trim();
  const seller = appointment.is_seller ? appointment.buyer : appointment.seller;
  const details = [
    `Visite du véhicule ${make} ${model}`,
    seller?.display_name ? `Contact : ${seller.display_name}` : '',
    seller?.phone ? `Tél : ${seller.phone}` : '',
  ].filter(Boolean).join('\n');

  const dtStart = toICSDateTime(appointment.date, appointment.time);
  const dtEnd = addMinutesToICS(appointment.date, appointment.time, 30);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${dtStart}/${dtEnd}`,
    details,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate a WhatsApp URL with a pre-filled appointment message
 */
export const getAppointmentWhatsAppUrl = (appointment, vehicle, phone) => {
  const make = vehicle?.make || '';
  const model = vehicle?.model || '';
  const dateFormatted = formatDateForMessage(appointment.date);

  const message = `Bonjour, je vous confirme notre rendez-vous pour le ${make} ${model} le ${dateFormatted} à ${appointment.time}.\nÀ bientôt !`;

  const cleanPhone = phone ? phone.replace(/[\s\-().]/g, '') : '';
  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
};

/**
 * Generate a mailto: URL with pre-filled subject and body
 */
export const getAppointmentEmailUrl = (appointment, vehicle, email) => {
  const make = vehicle?.make || '';
  const model = vehicle?.model || '';
  const dateFormatted = formatDateForMessage(appointment.date);

  const subject = `RDV - Visite ${make} ${model} le ${dateFormatted}`;
  const body = [
    `Bonjour,`,
    ``,
    `Je vous confirme notre rendez-vous pour le ${make} ${model} le ${dateFormatted} à ${appointment.time}.`,
    ``,
    `À bientôt !`,
  ].join('\n');

  const params = new URLSearchParams({ subject, body });
  const mailto = email ? `mailto:${email}` : 'mailto:';
  return `${mailto}?${params.toString()}`;
};
